import React from 'react'
import Dropdown from '../Dropdown/Dropdown'
import { ListResponse, Query } from '../../../../responses/response'
import { Observable } from 'rxjs'
import { useObservableState } from 'observable-hooks'
import { fromQueryWithReset } from '../../../../utils/rx-operators'
import { TypedAjaxResponse } from '../../../../http/types'
import { nullUnit, unit } from '../../../../utils/utils'
import { Entity } from '../../../../types/UtilityTypes'
import { entityToggle, getId } from '../../../../utils/entity-utils'

type SingleProps<T extends Entity> = {
    type: 'single'
    value?: T
    onChange: (value?: T) => void,
}

type NullableProps<T extends Entity> = {
    type: 'nullable',
    empty: string,
    value?: T | null,
    onChange: (value?: T | null) => void,
}

type MultiProps<T extends Entity> = {
    type: 'multi',
    value: T[],
    onChange: (value: T[]) => void,
}

type Props<T extends Entity> = {
    className?: string,
    placeholder?: string,
    render: (item: T) => string,
    request: (query: Query) => Observable<TypedAjaxResponse<ListResponse<T>>>,
} & (SingleProps<T> | NullableProps<T> | MultiProps<T>)

const RequestDropdown = <T extends Entity>(props: Props<T>): JSX.Element => {
    const [data, getData] = useObservableState(fromQueryWithReset(props.request), [])

    const onSearch = (search: string, reset: boolean) => getData({search, reset})

    switch (props.type){
        case 'multi':
            return <Dropdown
                key={'dropdown'}
                multi
                className={props.className}
                placeholder={props.placeholder}
                toKey={getId}
                data={data}
                selected={props.value}
                render={props.render}
                onChange={onSearch}
                onClear={() => props.onChange([])}
                onClick={item => props.onChange(entityToggle(item)(props.value))}
            />
        case 'single':
            return <Dropdown<T>
                key={'dropdown'}
                className={props.className}
                placeholder={props.placeholder}
                toKey={getId}
                data={data}
                selected={unit(props.value)}
                render={props.render}
                onChange={onSearch}
                onClear={() => props.onChange(undefined)}
                onClick={props.onChange}
            />
        case 'nullable':
            return <Dropdown<T | null>
                key={'dropdown'}
                className={props.className}
                placeholder={props.placeholder}
                toKey={item => item? item.id: 'null'}
                data={[null, ...data]}
                selected={nullUnit(props.value)}
                render={item => item? props.render(item): props.empty}
                onChange={onSearch}
                onClear={() => props.onChange(null)}
                onClick={props.onChange}
            />
    }
}

export default RequestDropdown